import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "focusOnHover"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { contains, closest } from 'ariakit-utils/dom';
import { hasFocusWithin } from 'ariakit-utils/focus';
import { useIsMouseMoving, useBooleanEvent, useEvent } from 'ariakit-utils/hooks';
import { useStore, createMemoComponent } from 'ariakit-utils/store';
import { createHook, createElement } from 'ariakit-utils/system';
import { C as CompositeContext } from '../__utils-7009759c.js';

function getMouseDestination(event) {
  var relatedTarget = event.relatedTarget;

  if ((relatedTarget == null ? void 0 : relatedTarget.nodeType) === Node.ELEMENT_NODE) {
    return relatedTarget;
  }

  return null;
}

function hoveringInside(event) {
  var nextElement = getMouseDestination(event);
  if (!nextElement) return false;
  return contains(event.currentTarget, nextElement);
}

function movingToAnotherItem(event) {
  var dest = getMouseDestination(event);
  if (!dest) return false;
  var item = closest(dest, "[data-composite-hover]");
  return !!item;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render an element in a composite widget that receives
 * focus on mouse move and loses focus to the composite base element on mouse
 * leave. This should be combined with the `CompositeItem` component, the
 * `useCompositeItem` hook or any component/hook that uses them underneath.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const state = useCompositeState();
 * const props = useCompositeHover({ state });
 * <CompositeItem state={state} {...props}>Item</CompositeItem>
 * ```
 */


var useCompositeHover = createHook(function (_ref) {
  var state = _ref.state,
      _ref$focusOnHover = _ref.focusOnHover,
      focusOnHover = _ref$focusOnHover === void 0 ? true : _ref$focusOnHover,
      props = _objectWithoutProperties(_ref, _excluded);

  state = useStore(state || CompositeContext, ["setActiveId", "baseRef"]);
  var isMouseMoving = useIsMouseMoving();
  var onMouseMoveProp = props.onMouseMove;
  var focusOnHoverProp = useBooleanEvent(focusOnHover);
  var onMouseMove = useEvent(function (event) {
    var _state2;

    onMouseMoveProp == null ? void 0 : onMouseMoveProp(event);
    if (event.defaultPrevented) return;
    if (!isMouseMoving()) return;
    if (!focusOnHoverProp(event)) return; // If we're hovering over an item that doesn't have DOM focus, we move
    // focus to the composite element. We're doing this here before setting
    // the active id because the composite element will automatically set the
    // active id to null when it receives focus.

    if (!hasFocusWithin(event.currentTarget)) {
      var _state, _state$baseRef$curren;

      (_state = state) == null ? void 0 : (_state$baseRef$curren = _state.baseRef.current) == null ? void 0 : _state$baseRef$curren.focus();
    }

    (_state2 = state) == null ? void 0 : _state2.setActiveId(event.currentTarget.id);
  });
  var onMouseLeaveProp = props.onMouseLeave;
  var onMouseLeave = useEvent(function (event) {
    var _state3, _state4, _state4$baseRef$curre;

    onMouseLeaveProp == null ? void 0 : onMouseLeaveProp(event);
    if (event.defaultPrevented) return;
    if (!isMouseMoving()) return;
    if (hoveringInside(event)) return;
    if (movingToAnotherItem(event)) return;
    if (!focusOnHoverProp(event)) return;
    (_state3 = state) == null ? void 0 : _state3.setActiveId(null); // Move focus to the composite element.

    (_state4 = state) == null ? void 0 : (_state4$baseRef$curre = _state4.baseRef.current) == null ? void 0 : _state4$baseRef$curre.focus();
  });
  props = _objectSpread(_objectSpread({
    "data-composite-hover": ""
  }, props), {}, {
    onMouseMove: onMouseMove,
    onMouseLeave: onMouseLeave
  });
  return props;
});
/**
 * A component that renders an element in a composite widget that receives focus
 * on mouse move and loses focus to the composite base element on mouse leave.
 * This should be combined with the `CompositeItem` component, the
 * `useCompositeItem` hook or any component/hook that uses them underneath.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeHover as={CompositeItem}>Item</CompositeHover>
 * </Composite>
 * ```
 */

var CompositeHover = createMemoComponent(function (props) {
  var htmlProps = useCompositeHover(props);
  return createElement("div", htmlProps);
});
export { CompositeHover, useCompositeHover };